import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Calendar as CalendarIcon, 
  Loader2, 
  Download,
  Clock,
  Users,
  Building,
  GraduationCap,
  Filter,
  BarChart3,
  FileText,
  Award
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, subYears } from 'date-fns';

const Reports = () => {
  usePageTitle('Reports');
  const navigate = useNavigate();
  
  // Filter states
  const [datePeriod, setDatePeriod] = useState('this_year');
  const [customDateStart, setCustomDateStart] = useState(null);
  const [customDateEnd, setCustomDateEnd] = useState(null);
  const [trainingCenterId, setTrainingCenterId] = useState('all');
  const [instructorId, setInstructorId] = useState('all');
  const [courseId, setCourseId] = useState('all');
  const [companyId, setCompanyId] = useState('all');
  
  // Data states
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [courses, setCourses] = useState([]);
  const [companies, setCompanies] = useState([]);
  
  // Report states
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);
  
  // Fetch reference data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tcRes, instRes, courseRes, compRes] = await Promise.all([
          axios.get(`${API_URL}/training-centers`),
          axios.get(`${API_URL}/instructors`),
          axios.get(`${API_URL}/courses`),
          axios.get(`${API_URL}/companies/lookup?limit=5000`)
        ]);
        setTrainingCenters(tcRes.data);
        setInstructors(instRes.data);
        setCourses(courseRes.data);
        setCompanies(compRes.data.items || compRes.data);
      } catch (error) {
        toast.error('Failed to load filter options');
      }
    };
    fetchData();
  }, []);
  
  // Get date range based on period
  const getDateRange = () => {
    const now = new Date();
    switch (datePeriod) {
      case 'this_month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case 'last_3_months':
        return { start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now) };
      case 'last_6_months':
        return { start: startOfMonth(subMonths(now, 5)), end: endOfMonth(now) };
      case 'this_year':
        return { start: startOfYear(now), end: endOfYear(now) };
      case 'last_year':
        const lastYear = subYears(now, 1);
        return { start: startOfYear(lastYear), end: endOfYear(lastYear) };
      case 'custom':
        return { start: customDateStart, end: customDateEnd };
      default:
        return { start: null, end: null };
    }
  };
  
  // Build filters object
  const buildFilters = () => {
    const dateRange = getDateRange();
    const filters = {};
    
    if (dateRange.start) {
      filters.date_start = format(dateRange.start, 'yyyy-MM-dd');
    }
    if (dateRange.end) {
      filters.date_end = format(dateRange.end, 'yyyy-MM-dd');
    }
    if (trainingCenterId && trainingCenterId !== 'all') {
      filters.training_center_id = trainingCenterId;
    }
    if (instructorId && instructorId !== 'all') {
      filters.instructor_id = instructorId;
    }
    if (courseId && courseId !== 'all') {
      filters.course_id = courseId;
    }
    if (companyId && companyId !== 'all') {
      filters.company_id = companyId;
    }
    
    return filters;
  };
  
  // Generate report
  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const filters = buildFilters();
      const response = await axios.post(`${API_URL}/reports/teaching-hours`, filters);
      setReport(response.data);
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };
  
  // Export report as PDF
  const handleExportReport = async () => {
    setExporting(true);
    try {
      const filters = buildFilters();
      const response = await axios.post(`${API_URL}/reports/teaching-hours/export`, filters, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const dateStr = format(new Date(), 'yyyyMMdd');
      link.setAttribute('download', `teaching_hours_report_${dateStr}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };
  
  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/admin-settings')}
            className="text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Settings
          </Button>
        </div>
      </div>
      
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Reports
        </h1>
        <p className="text-slate-500 mt-1">
          Generate instructor teaching hours reports
        </p>
      </div>
      
      {/* Filters Card */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5 text-[#2C9DE1]" />
            Report Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Period Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Period</Label>
              <Select value={datePeriod} onValueChange={setDatePeriod}>
                <SelectTrigger data-testid="report-period-select">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                  <SelectItem value="last_6_months">Last 6 Months</SelectItem>
                  <SelectItem value="this_year">This Year</SelectItem>
                  <SelectItem value="last_year">Last Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {datePeriod === 'custom' && (
              <>
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start font-normal">
                        <CalendarIcon className="w-4 h-4 mr-2 text-slate-500" />
                        {customDateStart ? format(customDateStart, 'dd MMM yyyy') : 'Select start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={customDateStart} onSelect={setCustomDateStart} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start font-normal">
                        <CalendarIcon className="w-4 h-4 mr-2 text-slate-500" />
                        {customDateEnd ? format(customDateEnd, 'dd MMM yyyy') : 'Select end date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={customDateEnd} onSelect={setCustomDateEnd} />
                    </PopoverContent>
                  </Popover>
                </div>
              </>
            )}
          </div>
          
          {/* Other Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Training Center</Label>
              <Select value={trainingCenterId} onValueChange={setTrainingCenterId}>
                <SelectTrigger data-testid="report-tc-select">
                  <SelectValue placeholder="All Centers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Training Centers</SelectItem>
                  {trainingCenters.map(tc => (
                    <SelectItem key={tc.id} value={tc.id}>{tc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Instructor</Label>
              <Select value={instructorId} onValueChange={setInstructorId}>
                <SelectTrigger data-testid="report-instructor-select">
                  <SelectValue placeholder="All Instructors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Instructors</SelectItem>
                  {instructors.map(inst => (
                    <SelectItem key={inst.id} value={inst.id}>
                      {inst.first_name} {inst.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Course</Label>
              <Select value={courseId} onValueChange={setCourseId}>
                <SelectTrigger data-testid="report-course-select">
                  <SelectValue placeholder="All Courses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Courses</SelectItem>
                  {courses.map(course => (
                    <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Company</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger data-testid="report-company-select">
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
              onClick={handleGenerateReport}
              disabled={loading}
              data-testid="generate-report-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
            
            {report && (
              <Button
                variant="outline"
                onClick={handleExportReport}
                disabled={exporting}
                data-testid="export-report-btn"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Export PDF
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Report Results */}
      {report && (
        <>
          {/* Period Info */}
          <div className="bg-slate-100 rounded-lg p-4">
            <p className="text-sm text-slate-600">
              <span className="font-semibold">Period:</span> {report.period_text}
            </p>
          </div>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card className="border-l-4 border-l-[#2C9DE1]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-[#2C9DE1]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Certificates</p>
                    <p className="text-2xl font-bold text-slate-900">{report.summary.total_certificates || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Classes</p>
                    <p className="text-2xl font-bold text-slate-900">{report.summary.total_classes}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Instructors</p>
                    <p className="text-2xl font-bold text-slate-900">{report.summary.total_instructors}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Hours</p>
                    <p className="text-2xl font-bold text-slate-900">{report.summary.total_teaching_hours}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Building className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Courses</p>
                    <p className="text-2xl font-bold text-slate-900">{report.summary.total_courses}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Main Teaching Hours Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#2C9DE1]" />
                Teaching Hours by Course and Instructor
                <Badge variant="outline" className="ml-2">{report.instructor_hours.length} records</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-[400px] overflow-auto rounded-lg border">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-50">
                    <TableRow>
                      <TableHead className="font-semibold">Course</TableHead>
                      <TableHead className="font-semibold">Instructor</TableHead>
                      <TableHead className="font-semibold text-right">Teaching Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.instructor_hours.length > 0 ? (
                      report.instructor_hours.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{item.course_name}</TableCell>
                          <TableCell>{item.instructor_name}</TableCell>
                          <TableCell className="text-right font-semibold">{item.teaching_hours} Hours</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                          No data found for the selected filters
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
          
          {/* Breakdowns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* By Instructor */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  Total Classes by Instructor
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-64 overflow-y-auto">
                {report.summary.by_instructor.length > 0 ? (
                  <div className="space-y-2">
                    {report.summary.by_instructor.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                        <span className="text-sm text-slate-700">{item.name}</span>
                        <div className="text-right">
                          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">{item.hours} Hours</Badge>
                          <span className="text-xs text-slate-500 ml-2">({item.class_count} classes)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No data</p>
                )}
              </CardContent>
            </Card>
            
            {/* By Course */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-emerald-600" />
                  Total by Course
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-64 overflow-y-auto">
                {report.summary.by_course.length > 0 ? (
                  <div className="space-y-2">
                    {report.summary.by_course.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                        <span className="text-sm text-slate-700 truncate flex-1 mr-2">{item.name}</span>
                        <div className="text-right">
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{item.hours} Hours</Badge>
                          <span className="text-xs text-slate-500 ml-2">({item.class_count} classes)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No data</p>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Company Certificates Breakdown */}
          {report.summary.by_company && report.summary.by_company.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building className="w-4 h-4 text-orange-600" />
                  Certificates by Company and Course
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-64 overflow-auto rounded-lg border">
                  <Table>
                    <TableHeader className="sticky top-0 bg-slate-50">
                      <TableRow>
                        <TableHead className="font-semibold">Company</TableHead>
                        <TableHead className="font-semibold">Course</TableHead>
                        <TableHead className="font-semibold text-right">Count</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.summary.by_company.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{item.company_name}</TableCell>
                          <TableCell>{item.course_name}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary">{item.certificate_count}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
